import React, { useEffect, useState } from 'react';
import { useProdAuth } from '../../contexts/ProdAuthContext';
import { Link } from 'react-router-dom';

const ACCENT = '#4A7C59';

interface StatCard {
  label: string;
  value: string | number;
  sub?: string;
  to?: string;
}

function Stat({ label, value, sub, to }: StatCard) {
  const content = (
    <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
      <p className="text-xs text-[#A3A3A3] uppercase tracking-widest mb-1">{label}</p>
      <p className="text-3xl font-bold text-[#1A1A1A]">{value}</p>
      {sub && <p className="text-xs text-[#6B6B6B] mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

// ── Field dashboard (Pollack — station supervisor) ────────────────────────────
function FieldDashboard() {
  const { user } = useProdAuth();
  const [today, setToday] = useState<{ deliveryCount: number; totalKg: string; totalAmount: string } | null>(null);
  const [farmers, setFarmers] = useState<string>('—');
  const [centres, setCentres] = useState<string>('—');

  useEffect(() => {
    Promise.all([
      fetch('/api/production/deliveries?summary=today', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/farmers?limit=1', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/collection-centres', { credentials: 'include' }).then(r => r.json()),
    ]).then(([t, f, c]) => {
      setToday(t);
      setFarmers(String(f.total ?? '—'));
      setCentres(String(c.centres?.length ?? '—'));
    }).catch(() => {});
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-1">Good day, {user?.name?.split(' ')[0]}</h2>
      <p className="text-sm text-[#6B6B6B] mb-8">Field · stations & farmer management</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Today's deliveries" value={today ? today.deliveryCount : '—'} sub={today ? `${parseFloat(today.totalKg || '0').toLocaleString()} kg` : undefined} to="/production/deliveries" />
        <Stat label="Today's value" value={today ? `KES ${parseFloat(today.totalAmount || '0').toLocaleString('en-KE', { maximumFractionDigits: 0 })}` : '—'} sub="paid to farmers" />
        <Stat label="Registered farmers" value={farmers} to="/production/farmers" />
        <Stat label="Active stations" value={centres} to="/production/centres" />
      </div>

      <div className="bg-white border border-[#E8E6E2] rounded-xl p-6">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Quick actions</h3>
        <div className="flex gap-3">
          <Link
            to="/production/deliveries/new"
            className="inline-flex items-center gap-2 px-5 py-3 text-white text-sm font-medium rounded-lg"
            style={{ backgroundColor: ACCENT }}
          >
            Record delivery
          </Link>
          <Link
            to="/production/centres"
            className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-lg border border-[#E8E6E2] text-[#1A1A1A] hover:bg-[#F0EDE8] transition-colors"
          >
            Manage stations
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Transport dashboard ───────────────────────────────────────────────────────
function TransportDashboard() {
  const { user } = useProdAuth();
  const [stats, setStats] = useState({ planned: '—', inTransit: '—', todayKg: '—', todayCount: '—' });

  useEffect(() => {
    Promise.all([
      fetch('/api/production/trips?status=planned&limit=1', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/trips?status=in_transit&limit=1', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/deliveries?summary=today', { credentials: 'include' }).then(r => r.json()),
    ]).then(([planned, inTransit, today]) => {
      setStats({
        planned: String(planned.total ?? 0),
        inTransit: String(inTransit.total ?? 0),
        todayKg: today.totalKg ? `${parseFloat(today.totalKg).toLocaleString()} kg` : '0 kg',
        todayCount: String(today.deliveryCount ?? 0),
      });
    }).catch(() => {});
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-1">Good day, {user?.name?.split(' ')[0]}</h2>
      <p className="text-sm text-[#6B6B6B] mb-8">Transport · cherry logistics</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Trips planned" value={stats.planned} to="/production/trips" />
        <Stat label="In transit" value={stats.inTransit} to="/production/trips" />
        <Stat label="Cherry today" value={stats.todayKg} to="/production/deliveries" />
        <Stat label="Deliveries today" value={stats.todayCount} to="/production/deliveries" />
      </div>

      <div className="bg-white border border-[#E8E6E2] rounded-xl p-6">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Quick actions</h3>
        <div className="flex gap-3">
          <Link
            to="/production/trips"
            className="inline-flex items-center gap-2 px-5 py-3 text-white text-sm font-medium rounded-lg"
            style={{ backgroundColor: ACCENT }}
          >
            Manage trips
          </Link>
          <Link
            to="/production/deliveries"
            className="inline-flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-lg border border-[#E8E6E2] text-[#1A1A1A] hover:bg-[#F0EDE8] transition-colors"
          >
            View deliveries
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Administration dashboard ──────────────────────────────────────────────────
function AdministrationDashboard() {
  const [stats, setStats] = useState({ totalBatches: '—', dryingBatches: '—', millingBatches: '—', completedBatches: '—' });
  const [pipelineCounts, setPipelineCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const statuses = ['cherry_intake', 'fermentation', 'drying', 'milling', 'grading', 'complete'];
    Promise.all([
      fetch('/api/production/batches?limit=1', { credentials: 'include' }).then(r => r.json()),
      ...statuses.map(s => fetch(`/api/production/batches?status=${s}&limit=1`, { credentials: 'include' }).then(r => r.json())),
    ]).then(([all, ...statusResults]) => {
      setStats({
        totalBatches: String(all.total ?? '—'),
        dryingBatches: String(statusResults[2].total ?? 0),
        millingBatches: String(statusResults[3].total ?? 0),
        completedBatches: String(statusResults[5].total ?? 0),
      });
      const counts: Record<string, number> = {};
      statuses.forEach((s, i) => { counts[s] = Number(statusResults[i].total ?? 0); });
      setPipelineCounts(counts);
    }).catch(() => {});
  }, []);

  const PIPELINE_STAGES = [
    { key: 'cherry_intake', label: 'Cherry intake' },
    { key: 'fermentation', label: 'Fermentation' },
    { key: 'drying', label: 'Drying' },
    { key: 'milling', label: 'Milling' },
    { key: 'grading', label: 'Grading' },
    { key: 'complete', label: 'Complete' },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-1">Administration</h2>
      <p className="text-sm text-[#6B6B6B] mb-8">Processing pipeline · inventory · warehouse</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Total batches" value={stats.totalBatches} to="/production/batches" />
        <Stat label="On drying beds" value={stats.dryingBatches} to="/production/drying" />
        <Stat label="In milling" value={stats.millingBatches} to="/production/batches" />
        <Stat label="Completed" value={stats.completedBatches} to="/production/batches" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8E6E2] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Pipeline</h3>
          <div className="space-y-2">
            {PIPELINE_STAGES.map(({ key, label }) => {
              const count = pipelineCounts[key] ?? 0;
              return (
                <Link
                  key={key}
                  to={`/production/batches?status=${key}`}
                  className="flex items-center justify-between py-1.5 hover:opacity-75 transition-opacity"
                >
                  <span className="text-sm text-[#6B6B6B]">{label}</span>
                  <div className="flex items-center gap-3">
                    {count > 0 && <div className="h-1.5 rounded-full bg-[#4A7C59]" style={{ width: `${Math.min(count * 8, 80)}px` }} />}
                    <span className={`text-sm font-bold w-6 text-right ${count > 0 ? 'text-[#1A1A1A]' : 'text-[#E8E6E2]'}`}>{count}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E2] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Quick actions</h3>
          <div className="space-y-2">
            {[
              { to: '/production/batches/new', label: 'New batch', primary: true },
              { to: '/production/batches', label: 'All batches', primary: false },
              { to: '/production/drying', label: 'Drying beds', primary: false },
              { to: '/production/inventory', label: 'Inventory', primary: false },
              { to: '/production/reports', label: 'Reports', primary: false },
            ].map(({ to, label, primary }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors group ${primary ? 'text-white' : 'hover:bg-[#F0EDE8] text-[#1A1A1A]'}`}
                style={primary ? { backgroundColor: ACCENT } : {}}
              >
                <span className="text-sm">{label}</span>
                {!primary && <svg className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#1A1A1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Admin dashboard ───────────────────────────────────────────────────────────
interface CentreRow {
  id: string;
  name: string;
  centreType: string | null;
  managerName: string | null;
  managerPhone: string | null;
  active: boolean;
  farmerCount: number;
  todayDeliveryCount: number;
  todayKg: string;
  activeBatches: number;
  managerUser: { name: string; email: string } | null;
}

function AdminDashboard() {
  const [stats, setStats] = useState({
    farmers: '—', centres: '—', totalBatches: '—',
    todayDeliveries: '—', todayKg: '—',
  });
  const [pipelineCounts, setPipelineCounts] = useState<Record<string, number>>({});
  const [centreRows, setCentreRows] = useState<CentreRow[]>([]);

  useEffect(() => {
    const statuses = ['cherry_intake', 'fermentation', 'drying', 'milling', 'grading', 'complete'];
    Promise.all([
      fetch('/api/production/farmers?limit=1', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/collection-centres', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/batches?limit=1', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/production/deliveries?summary=today', { credentials: 'include' }).then(r => r.json()),
      ...statuses.map(s => fetch(`/api/production/batches?status=${s}&limit=1`, { credentials: 'include' }).then(r => r.json())),
    ]).then(([farmers, centres, batches, today, ...statusResults]) => {
      const allCentres: CentreRow[] = centres.centres || [];
      setStats({
        farmers: String(farmers.total ?? '—'),
        centres: String(allCentres.length),
        totalBatches: String(batches.total ?? '—'),
        todayDeliveries: String(today.deliveryCount ?? 0),
        todayKg: today.totalKg ? `${parseFloat(today.totalKg).toLocaleString()} kg` : '0 kg',
      });
      setCentreRows(allCentres);
      const counts: Record<string, number> = {};
      statuses.forEach((s, i) => { counts[s] = Number(statusResults[i].total ?? 0); });
      setPipelineCounts(counts);
    }).catch(() => {});
  }, []);

  const PIPELINE_STAGES = [
    { key: 'cherry_intake', label: 'Cherry intake' },
    { key: 'fermentation', label: 'Fermentation' },
    { key: 'drying', label: 'Drying' },
    { key: 'milling', label: 'Milling' },
    { key: 'grading', label: 'Grading' },
    { key: 'complete', label: 'Complete' },
  ];

  const stations = centreRows.filter(c => c.centreType !== 'hub' && c.active);
  const todayTotalKg = stations.reduce((sum, c) => sum + parseFloat(c.todayKg || '0'), 0);
  const todayTotalDeliveries = stations.reduce((sum, c) => sum + c.todayDeliveryCount, 0);

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-1">Hub overview</h2>
      <p className="text-sm text-[#6B6B6B] mb-8">Field operations · daily production</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat label="Total farmers" value={stats.farmers} to="/production/farmers" />
        <Stat label="Centres" value={stats.centres} to="/production/centres" />
        <Stat label="Total batches" value={stats.totalBatches} to="/production/batches" />
        <Stat label="Deliveries today" value={stats.todayDeliveries} to="/production/deliveries" />
        <Stat label="Cherry today" value={stats.todayKg} to="/production/deliveries" />
      </div>

      {/* ── Station breakdown ──────────────────────────────────────────────── */}
      <div className="bg-white border border-[#E8E6E2] rounded-xl mb-6 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E6E2]">
          <h3 className="text-sm font-semibold text-[#1A1A1A]">Field stations — today</h3>
          <Link to="/production/centres" className="text-xs text-[#4A7C59] hover:underline">Manage stations →</Link>
        </div>
        {stations.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#A3A3A3] text-center">No active stations yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E6E2]">
                  {['Station', 'Manager', 'Farmers', 'Deliveries', 'Cherry today', 'Active batches'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[#A3A3A3] uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stations.map(c => (
                  <tr key={c.id} className="border-b border-[#E8E6E2] last:border-0 hover:bg-[#F9F8F6] transition-colors">
                    <td className="px-4 py-3 font-medium text-[#1A1A1A] whitespace-nowrap">{c.name}</td>
                    <td className="px-4 py-3 text-[#6B6B6B] whitespace-nowrap">
                      {c.managerUser?.name || c.managerName || <span className="text-[#C4C0BB] italic">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3 text-[#1A1A1A]">{c.farmerCount}</td>
                    <td className="px-4 py-3 text-[#1A1A1A]">
                      {c.todayDeliveryCount > 0
                        ? <span className="font-medium text-[#4A7C59]">{c.todayDeliveryCount}</span>
                        : <span className="text-[#C4C0BB]">0</span>}
                    </td>
                    <td className="px-4 py-3 text-[#1A1A1A] whitespace-nowrap">
                      {parseFloat(c.todayKg || '0') > 0
                        ? <span className="font-medium">{parseFloat(c.todayKg).toLocaleString()} kg</span>
                        : <span className="text-[#C4C0BB]">— kg</span>}
                    </td>
                    <td className="px-4 py-3">
                      {c.activeBatches > 0
                        ? <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold">
                            {c.activeBatches}
                          </span>
                        : <span className="text-[#C4C0BB] text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {stations.length > 1 && (
                <tfoot>
                  <tr className="bg-[#F9F8F6]">
                    <td className="px-4 py-3 text-xs font-semibold text-[#6B6B6B] uppercase tracking-wide" colSpan={2}>Total</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#1A1A1A]">
                      {stations.reduce((s, c) => s + c.farmerCount, 0)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-[#1A1A1A]">{todayTotalDeliveries}</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#1A1A1A]">{todayTotalKg.toLocaleString()} kg</td>
                    <td className="px-4 py-3 text-sm font-bold text-[#1A1A1A]">
                      {stations.reduce((s, c) => s + c.activeBatches, 0)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick links */}
        <div className="bg-white border border-[#E8E6E2] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Quick links</h3>
          <div className="space-y-1">
            {[
              { to: '/production/farmers', label: 'Farmer registry' },
              { to: '/production/deliveries', label: 'Cherry deliveries' },
              { to: '/production/batches', label: 'Processing batches' },
              { to: '/production/drying', label: 'Drying beds' },
              { to: '/production/users', label: 'System users' },
              { to: '/production/reports', label: 'Season reports' },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-[#F0EDE8] transition-colors group"
              >
                <span className="text-sm text-[#1A1A1A]">{label}</span>
                <svg className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#1A1A1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </div>

        {/* Live pipeline */}
        <div className="bg-white border border-[#E8E6E2] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-4">Pipeline — batch counts</h3>
          <div className="space-y-2">
            {PIPELINE_STAGES.map(({ key, label }) => {
              const count = pipelineCounts[key] ?? 0;
              return (
                <Link
                  key={key}
                  to={`/production/batches?status=${key}`}
                  className="flex items-center justify-between py-1.5 hover:opacity-75 transition-opacity"
                >
                  <span className="text-sm text-[#6B6B6B]">{label}</span>
                  <div className="flex items-center gap-3">
                    {count > 0 && (
                      <div className="h-1.5 rounded-full bg-[#4A7C59]" style={{ width: `${Math.min(count * 8, 80)}px` }} />
                    )}
                    <span className={`text-sm font-bold w-6 text-right ${count > 0 ? 'text-[#1A1A1A]' : 'text-[#E8E6E2]'}`}>
                      {count}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Role router ───────────────────────────────────────────────────────────────
export const ProductionDashboard: React.FC = () => {
  const { user } = useProdAuth();
  if (!user) return null;

  switch (user.role) {
    case 'field':
      return <FieldDashboard />;
    case 'transport':
      return <TransportDashboard />;
    case 'administration':
      return <AdministrationDashboard />;
    case 'admin':
    case 'super_admin':
      return <AdminDashboard />;
    default:
      return <FieldDashboard />;
  }
};
